
import type { CaptureSequence } from './types';

// (BlazePose)
export const IDX = {
  NOSE: 0, L_EAR: 7, R_EAR: 8, L_SHO: 11, R_SHO: 12, L_ELB: 13, R_ELB: 14,
  L_WR: 15, R_WR: 16, L_HIP: 23, R_HIP: 24, L_KNEE: 25, R_KNEE: 26, L_ANK: 27,
  R_ANK: 28, L_HEEL: 29, R_HEEL: 30, L_FI: 31, R_FI: 32,
};

export const IDEAL = {
  toeOutDeg: 10,
  hkaDeg: 180,
  cvMinDeg: 50,
  cvAngle: 50, // Ideally 50 degrees or more for good posture
  kneeDeg: 180,
};

export const VIEW_SEQUENCE: CaptureSequence[] = ['Anterior', 'Posterior', 'Left', 'Right'];

/**
 * Returns the view sequence for capture/display based on experimental mode.
 * When experimental features are disabled, Posterior (back) view is excluded.
 * Order: Anterior, (Posterior when experimental), Left, Right.
 */
export function getEffectiveViewSequence(experimentalEnabled: boolean): CaptureSequence[] {
  return experimentalEnabled ? VIEW_SEQUENCE : (['Anterior', 'Left', 'Right'] as CaptureSequence[]);
}

export const SEVERITY_BANDS = {
  // Unified bands: <5 Green, 5-8 Orange, >8 Red
  default: [5, 8, 12] as [number, number, number],
  headTilt: [5, 8, 12] as [number, number, number],
  pelvicShift: (hipWidthCM: number) => [hipWidthCM * 0.03 || 0.5, hipWidthCM * 0.06 || 1.0, hipWidthCM * 0.1 || 2.0] as [number, number, number], // Keep relative for now, or switch to unified absolute? Let's stick to unified absolute if possible, but cm vs deg differs.
  // User requested "1-4 Green, 5-7 Orange, 8-10 Red". This is likely for degrees.
  // For cm, 5cm is huge.
  // Let's apply [5, 8, 12] for Angle based metrics.
  toeOut: [5, 8, 12] as [number, number, number],
  hka: [5, 8, 12] as [number, number, number],
  calcTilt: [5, 8, 12] as [number, number, number],
  cvAngle: [5, 8, 12] as [number, number, number], 
  fwdShift: [1, 3, 5] as [number, number, number], // Shift in CM needs smaller bands usually. 1-3cm Green? 3-5 Orange? 5+ Red?
  kneeAngle: [5, 8, 12] as [number, number, number],
  pelvicTilt: [5, 8, 12] as [number, number, number],
  tibialInclination: [5, 8, 12] as [number, number, number],
};

export const MASS_DISTRIBUTION = {
  head: 0.08, armL: 0.05, armR: 0.05, trunk: 0.40, hipL: 0.06, hipR: 0.06,
  thighL: 0.10, thighR: 0.10, shankL: 0.05, shankR: 0.05,
};

export const HELP_TEXT: Record<string, string> = {
  'digi-frontal': `Frontal View\n• Rows show side-to-side deltas (reported as “Tilted Right/Left”).\n• Values use your selected unit. Badge: 0 OK, 1 Minor, 2 Moderate, 3 Severe.`,
  'digi-lateral': `Lateral View\n• FWD/Back measured from ankle plumb line to landmark (positive = forward).\n• Pelvic tilt uses ASIS→PSIS line if provided.\n• Tip: Use the 🎯 button to place pelvis landmarks; pins appear immediately.`,
  'digi-rotation': `Rotation View\n• Signed degrees: positive = rotation toward Right, negative = Left.\n• Head Tilt uses right/left lowering in degrees.`,
  'digi-com': `Center of Mass\n• Dot shows COM vs mid-feet (Right/Left) and Front/Back axes.\n• Numbers honor unit switch.`,
  'digi-class': `Posture Classification\n• Default orientation algorithm: “Horizontal Displacement + Visual Pattern Recognition” (hip vs ankle plumb plus head/trunk signals; optional ASIS/PSIS line).\n• Alternate: “Clinical score rules (legacy default)” — the prior YAML-threshold scoreboard.\n• Lateralization still uses trunk/shoulder/hip signed measures.\n• Pick the algorithm in Capture Settings (gear) → Posture classification.`,
  'panel-Anterior': `Anterior Panel\n• Δ from ideal (0). “Tilted Right/Left” shows which side is lower.`,
  'panel-Posterior': `Posterior Panel\n• Δ from ideal; calcaneus tilt uses inversion/eversion convention.`,
  'panel-Left': `Left Lateral Panel\n• FWD/Back vs ankle plumb.\n• Use 🎯 to place ASIS then PSIS (pins show instantly).`,
  'panel-Right': `Right Lateral Panel\n• Same as left.`
};
